'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useSocket } from '@/hooks/useSocket';
import { EventResponse } from '@/types';

/**
 * useSportsMarketSocket: Processes real-time price change events from the CLOB WebSocket
 * and updates the sports query cache.
 */
export const useSportsMarketSocket = (initialEvents: EventResponse[] = []) => {
  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (data) => {
      // Only process price_change events
      if (data.event_type !== 'price_change') return;

      const { market: marketConditionId, price_changes } = data as {
        market: string;
        price_changes: Array<{
          asset_id: string;
          price: string;
          best_bid: string;
          best_ask: string;
        }>;
      };

      if (!price_changes?.length) return;

      const assetPriceMap = new Map(
        price_changes.map((pc) => [
          pc.asset_id,
          {
            price: pc.price,
            bestBid: pc.best_bid,
            bestAsk: pc.best_ask,
            timestamp: Date.now(),
          },
        ]),
      );

      /**
       * updateEvents: Recursive helper to patch market events in the cache.
       */
      const updateEvents = (
        events: EventResponse[],
      ): { items: EventResponse[]; hasChanges: boolean } => {
        let hasChangesTotal = false;
        const items = events.map((event) => {
          let eventHasChanges = false;
          const updatedMarkets = event.markets?.map((market) => {
            let tokenIds: string[] = [];
            if (typeof market.clobTokenIds === 'string') {
              try {
                const parsed = JSON.parse(market.clobTokenIds);
                tokenIds = Array.isArray(parsed) ? parsed : [market.clobTokenIds];
              } catch {
                tokenIds = [market.clobTokenIds];
              }
            } else if (Array.isArray(market.clobTokenIds)) {
              tokenIds = market.clobTokenIds;
            } else if (market.clobTokenIds) {
              tokenIds = [market.clobTokenIds as string];
            }

            const hasMatchedAsset = tokenIds.some((id) => assetPriceMap.has(id));

            if (market.conditionId !== marketConditionId && !hasMatchedAsset) return market;

            eventHasChanges = true;
            hasChangesTotal = true;

            const nextMarkets = { ...market };

            let parsedOutcomes: any[] = [];
            try {
              parsedOutcomes =
                typeof market.outcomes === 'string'
                  ? JSON.parse(market.outcomes)
                  : market.outcomes || [];
            } catch (e) {
              // ignore parse error
            }

            let outcomesUpdated = false;

            tokenIds.forEach((tokenId, tokenIndex) => {
              if (assetPriceMap.has(tokenId)) {
                const update = assetPriceMap.get(tokenId)!;
                outcomesUpdated = true;

                let oldPrice = 0;
                if (parsedOutcomes[tokenIndex]) {
                  oldPrice = parseFloat(parsedOutcomes[tokenIndex].price);
                  parsedOutcomes[tokenIndex] = {
                    ...parsedOutcomes[tokenIndex],
                    price: update.price,
                    bestBid: update.bestBid,
                    bestAsk: update.bestAsk,
                  };
                }

                const newPrice = parseFloat(update.price);

                if (Array.isArray(market.outcomePrices)) {
                  const prices = nextMarkets.outcomePrices
                    ? [...nextMarkets.outcomePrices]
                    : [...market.outcomePrices];
                  prices[tokenIndex] = update.price;
                  nextMarkets.outcomePrices = prices as any;
                }

                if (!nextMarkets.priceMetadata) nextMarkets.priceMetadata = {};
                nextMarkets.priceMetadata[tokenId] = {
                  direction: newPrice > oldPrice ? 'up' : newPrice < oldPrice ? 'down' : null,
                  timestamp: update.timestamp,
                  bestBid: update.bestBid,
                  bestAsk: update.bestAsk,
                };
              }
            });

            if (outcomesUpdated) {
              nextMarkets.outcomes = parsedOutcomes;
            }

            return nextMarkets;
          });

          return eventHasChanges ? { ...event, markets: updatedMarkets } : event;
        });

        return { items, hasChanges: hasChangesTotal };
      };

      // Update all queries that start with 'sports'
      queryClient.setQueriesData({ queryKey: ['sports'] }, (oldData: any) => {
        if (!oldData) return oldData;

        // Support for infinite queries, paginated results, and simple arrays
        if (oldData.pages) {
          let anyChanges = false;
          const newPages = oldData.pages.map((page: any) => {
            const { items, hasChanges } = updateEvents(page.items || []);
            if (hasChanges) anyChanges = true;
            return { ...page, items };
          });
          return anyChanges ? { ...oldData, pages: newPages } : oldData;
        }

        const isArray = Array.isArray(oldData);
        const isSingleEvent = oldData && oldData.markets && !oldData.items;

        let eventsToUpdate;
        if (isArray) {
          eventsToUpdate = oldData;
        } else if (isSingleEvent) {
          eventsToUpdate = [oldData];
        } else {
          eventsToUpdate = oldData.items || [];
        }

        const { items, hasChanges } = updateEvents(eventsToUpdate);

        if (!hasChanges) return oldData;
        if (isArray) return items;
        if (isSingleEvent) return items[0];
        return { ...oldData, items };
      });

      // Update markets prices query data as well
      queryClient.setQueriesData({ queryKey: ['markets', 'prices'] }, (oldData: any) => {
        if (!oldData) return oldData;
        let hasChanges = false;
        const nextData = { ...oldData };

        price_changes.forEach((pc) => {
          if (nextData[pc.asset_id]) {
            const bidPrice = parseFloat(pc.best_bid);
            const askPrice = parseFloat(pc.best_ask);
            nextData[pc.asset_id] = {
              bidPrice,
              askPrice,
              midPrice: (bidPrice + askPrice) / 2,
              spread: askPrice - bidPrice,
            };
            hasChanges = true;
          }
        });

        return hasChanges ? nextData : oldData;
      });
    },
    [queryClient],
  );

  const { isConnected, send } = useSocket('crypto', handleMessage);

  const tokenIdsString = useMemo(() => {
    if (initialEvents.length === 0) return '';
    const ids = initialEvents
      .flatMap(
        (event) =>
          event.markets?.flatMap((market) => {
            let tokens: string[] = [];
            const rawTokens = market.clobTokenIds;
            if (typeof rawTokens === 'string') {
              try {
                // Sometimes clobTokenIds is a JSON string array like "['123', '456']"
                tokens = rawTokens.startsWith('[') ? JSON.parse(rawTokens) : [rawTokens];
              } catch (e) {
                tokens = [rawTokens];
              }
            } else if (Array.isArray(rawTokens)) {
              tokens = rawTokens;
            }
            return tokens;
          }) || [],
      )
      .filter((id, index, self) => id && self.indexOf(id) === index)
      .sort();
    return JSON.stringify(ids);
  }, [initialEvents]);

  // Auto-subscribe to all relevant tokens on connection or cache change
  useEffect(() => {
    if (!isConnected || !tokenIdsString) return;

    try {
      const clobTokenIds = JSON.parse(tokenIdsString) as string[];
      if (clobTokenIds.length > 0) {
        send({
          assets_ids: clobTokenIds,
          type: 'market',
        });
      }
    } catch (e) {
      console.error('Error parsing tokenIdsString:', e);
    }
  }, [isConnected, tokenIdsString, send]);

  return { isConnected };
};
